import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
    IEnrolement,
    IInvoices,
    ITaxFormsUploadResponse,
    INDAFormUploadResponse,
    IVoidChequeUploadResponse
} from './enrole.interface';
import { inject, Injectable } from '@angular/core';
import { EnrolementService } from './enrole.service';
import { tap, catchError } from 'rxjs';
import {
    GetEnrolementForms,
    CreateEnrolement,
    GetSingleEnrolementForm,
    GetAllEnrolementInvoices,
    GetSingleEnrolementInvoices,
    UploadEnrolementInvoice,
    GetUsereEnrolementForm,
    SubmitEnrolementInvoice,
    UploadTaxForms,
    UploadNDAForm,
    UploadVoidCheque
} from './enrole.action';

export class IProps {
    enrolements!: IEnrolement[];
    invoices!: IInvoices[];
    invoiceUpload!: string[];
    enrolement!: IEnrolement | null;
    userEnrolement!: IEnrolement | null;
    invoice!: IInvoices | null;
    loading!: boolean;
    taxFormsUpload!: ITaxFormsUploadResponse | null;
    ndaFormUpload!: INDAFormUploadResponse | null;
    voidChequeUpload!: IVoidChequeUploadResponse | null;
}

const defaults = {
    enrolements: [],
    invoices: [],
    invoiceUpload: [],
    enrolement: null,
    userEnrolement: null,
    invoice: null,
    loading: false,
    taxFormsUpload: null,
    ndaFormUpload: null,
    voidChequeUpload: null
};

@State<IProps>({
    name: 'enrolement',
    defaults
})
@Injectable()
export class EnrolementStore {
    enrolementService: EnrolementService = inject(EnrolementService);

    @Selector()
    static getEnrolements(state: IProps) {
        return state.enrolements;
    }
    @Selector()
    static getUploads(state: IProps) {
        return state.invoiceUpload;
    }
    @Selector()
    static getSingleEnrolement(state: IProps) {
        return state.enrolement;
    }
    @Selector()
    static getUserEnrolement(state: IProps) {
        return state.userEnrolement;
    }
    @Selector()
    static loading(state: IProps) {
        return state.loading;
    }
    @Selector()
    static getInvoices(state: IProps) {
        return state.invoices;
    }

    static getSingleInvoice(state: IProps) {
        return state.invoice;
    }
    @Selector()
    static getLoading(state: IProps) {
        return state.loading;
    }
    @Selector()
    static getTaxFormsUpload(state: IProps) {
        return state.taxFormsUpload;
    }
    @Selector()
    static getNDAFormUpload(state: IProps) {
        return state.ndaFormUpload;
    }
    @Selector()
    static getVoidChequeUpload(state: IProps) {
        return state.voidChequeUpload;
    }

    @Action(GetEnrolementForms)
    get(ctx: StateContext<IProps>, action: GetEnrolementForms) {
        ctx.patchState({ loading: true });
        return this.enrolementService.getAllEnrolements().pipe(
            tap((res) => {
                ctx.patchState({
                    enrolements: res,
                    loading: false
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false
                });
                throw err;
            })
        );
    }

    @Action(CreateEnrolement)
    create(ctx: StateContext<IProps>, action: CreateEnrolement) {
        const state = ctx.getState();

        return this.enrolementService.createEnrolement(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    enrolements: [res, ...state.enrolements]
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(GetSingleEnrolementForm)
    getSingleEnrolement(ctx: StateContext<IProps>, action: GetSingleEnrolementForm) {
        ctx.patchState({ loading: true });

        return this.enrolementService.getSingleEnrolement(action.id).pipe(
            tap((res) => {
                ctx.patchState({
                    enrolement: res,
                    loading: false
                }),
                    catchError((err) => {
                        ctx.patchState({
                            loading: false
                        });
                        throw err;
                    });
            })
        );
    }

    @Action(GetUsereEnrolementForm)
    getUserEnrolement(ctx: StateContext<IProps>, action: GetUsereEnrolementForm) {
        ctx.patchState({ loading: true });

        return this.enrolementService.getUserEnrolement(action.userId).pipe(
            tap((res) => {
                ctx.patchState({
                    userEnrolement: res,
                    loading: false
                }),
                    catchError((err) => {
                        ctx.patchState({
                            loading: false
                        });
                        throw err;
                    });
            })
        );
    }

    @Action(GetAllEnrolementInvoices)
    getInvoices(ctx: StateContext<IProps>, action: GetAllEnrolementInvoices) {
        ctx.patchState({ loading: true });
        return this.enrolementService.getAllInvoices().pipe(
            tap((res) => {
                ctx.patchState({
                    invoices: res,
                    loading: false
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false
                });
                throw err;
            })
        );
    }

    @Action(UploadEnrolementInvoice)
    upload(ctx: StateContext<IProps>, action: UploadEnrolementInvoice) {
        const state = ctx.getState();

        return this.enrolementService.uploadeInvoice(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    invoiceUpload: res
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(SubmitEnrolementInvoice)
    submitInvoce(ctx: StateContext<IProps>, action: SubmitEnrolementInvoice) {
        const state = ctx.getState();

        return this.enrolementService.submitInvoice(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    invoices: [res, ...state.invoices]
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(GetSingleEnrolementInvoices)
    getSingleInvoice(ctx: StateContext<IProps>, action: GetSingleEnrolementInvoices) {
        ctx.patchState({ loading: true });

        return this.enrolementService.getSingleInvoice(action.id).pipe(
            tap((res) => {
                ctx.patchState({
                    invoice: res,
                    loading: false
                }),
                    catchError((err) => {
                        ctx.patchState({
                            loading: false
                        });
                        throw err;
                    });
            })
        );
    }

    @Action(UploadTaxForms)
    uploadTaxForms(ctx: StateContext<IProps>, action: UploadTaxForms) {
        return this.enrolementService.uploadTaxForms(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    taxFormsUpload: res
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(UploadNDAForm)
    uploadNDAForm(ctx: StateContext<IProps>, action: UploadNDAForm) {
        return this.enrolementService.uploadNDAForm(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    ndaFormUpload: res
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(UploadVoidCheque)
    uploadVoidCheque(ctx: StateContext<IProps>, action: UploadVoidCheque) {
        return this.enrolementService.uploadVoidCheque(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    voidChequeUpload: res
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }
}
